<template>
    <LoadingContainer :promise="containerPromise" v-slot="container">
        <li class="list-group-item py-0 pr-0 pl-0">
            <div class="d-flex">
                <div class="col-4 role-section" :style="{ 'padding-left': depth * 0.75 + 1 + 'rem' }">
                    <div class="role-editor-container-label">{{ container.DisplayLabel }}</div>
                </div>
                <div class="col-8 d-flex justify-content-between role-section">
                    <ul class="d-inline-block p-0">
                        <li class="badge font-italic fw-500 py-1" v-if="(activeRoles || []).length + (implicitRoles || []).length == 0">None</li>
                        <li class="badge badge-light mr-1" v-for="role in activeRoles">{{ getRoleDisplayLabel(role.Container, role.RoleName) }}</li>
                        <li class="badge badge-light badge-disabled mr-1" v-for="role in onlyImplicitRoles">
                            {{ getRoleDisplayLabel(role.Container, role.RoleName) }}
                            <HelpIcon :text="'Granted implicitly through ' + getRoleDisplayLabel(role.Cause.Container, role.Cause.RoleName) + ' at ' + getContainerLabel(role.Cause.Container)" :placement="'right'" />
                        </li>
                    </ul>
                    <button class="btn btn-sm btn-bare icon icon-edit d-inline-block" :id="container.Code + '_OpenEditBtn'" @click="editing = true" v-if="!editing" aria-label="Edit"></button>
                </div>
            </div>
            <div class="role-editing-section" v-show="editing">
                <button type="button" class="close icon icon-close" @click="editing = false"><span class="sr-only">Close</span></button>
                <h6 class="mb-3">{{ container.DisplayLabel }} Roles</h6>
                <div class="form-check" v-for="role in orderedRoles">
                    <label :for="container.Code + '_' + role.Name" class="form-check-label d-inline-flex w-100">
                        <span class="col-md-3">
                            <input :id="container.Code + '_' + role.Name" v-model="editRoles" :indeterminate.prop="!editRoles.includes(role.Name) && implicitEditRoles.includes(role.Name)" :value="role.Name" type="checkbox" class="form-check-input" :disabled="role.IsDisabled" />
                            {{ role.DisplayLabel == null ? role.Name : role.DisplayLabel }}
                        </span>
                        <span class="col-md-9 text-muted">{{ role.Description }}</span>
                    </label>
                </div>
                <div class="pt-3">
                    <button class="btn btn-sm btn-primary" @click="save" :id="container.Code + '_SaveEditBtn'">Save</button>
                    <button class="btn btn-sm btn-bare" @click="editing = false" :id="container.Code + '_CancelEditBtn'">Cancel</button>
                    <AsyncOperationStatus :promise="savePromise" />
                </div>
            </div>
            <ul class="list-group list-group-flush role-editor-list">
                <RoleContainer :key="subcontainer.Code" :container="subcontainer" :depth="depth + 1" v-for="subcontainer in container.Subtenants" />
            </ul>
        </li>
    </LoadingContainer>
</template>
<script lang="ts">
import { defineComponent, ref, computed, inject, watch } from "vue";
import { throwIfNonSuccessStatus } from "@/lib/fetchHelpers.3";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import LoadingContainer from "@/lib/LoadingContainer.3.vue";
import { HierarchyType } from "@cs/HierarchyType";
import HelpIcon from "@/lib/HelpIcon.3.vue";
import { objectDistinct } from "@/lib/distinct";
import { activeContainerRolesInjectKey, implicitContainerRolesInjectKey, possibleContainerRolesInjectKey, getContainerLabelInjectKey, getRoleDisplayLabelInjectKey, setContainerRoleNamesInjectKey } from "./Users.3.vue.InjectKeys";
import { computedAsync } from "@vueuse/core";
import { useMainStore } from "@/mainStore";
import { TenantHierarchyData } from "@cs/TenantHierarchyData";
import { RoleDto } from "@cs/RoleDto";
import { RoleDtoExtended } from "./UserModalMixin";
interface Props {
    depth: number;
    container: (TenantHierarchyData & { subtenants: TenantHierarchyData[] }) | null;
    rootCode?: string;
}

export default defineComponent({
    name: "RoleContainer",
    components: {
        AsyncOperationStatus,
        LoadingContainer,
        HelpIcon,
    },
    props: ["depth", "container", "rootCode"],
    setup(props: Props) {
        const mainStore = useMainStore();

        const possibleContainerRoles = inject(possibleContainerRolesInjectKey)!;
        const activeContainerRoles = inject(activeContainerRolesInjectKey)!;
        const implicitContainerRoles = inject(implicitContainerRolesInjectKey)!;
        const setContainerRoleNames = inject(setContainerRoleNamesInjectKey)!;
        const getContainerLabel = inject(getContainerLabelInjectKey)!;
        const getRoleDisplayLabel = inject(getRoleDisplayLabelInjectKey)!;

        const rootContainerPromise = ref<Promise<TenantHierarchyData>>(null!);
        const editing = ref(false);
        const editRoles = ref<string[]>([]);
        const implicitEditRoles = ref<string[]>([]);
        const savePromise = ref<Promise<void> | null>(null);

        const containerPromise = computed(async () => {
            if (props.container == null && rootContainerPromise.value != null) {
                return await rootContainerPromise.value;
            } else {
                return props.container;
            }
        });
        const orderedRoles = computed(() => {
            function compare(a: RoleDto, b: RoleDto) {
                if (a.DisplayLabel < b.DisplayLabel) {
                    return -1;
                }
                if (a.DisplayLabel > b.DisplayLabel) {
                    return 1;
                }
                return 0;
            }
            return (possibleRoles.value || [])
                .slice(0)
                .filter(i => !isRoleHidden(i.Name))
                .sort(compare);
        });
        const possibleRoles = computed(() => {
            if (resolvedContainer.value == null) {
                return null;
            }
            return possibleContainerRoles(resolvedContainer.value.Code);
        });
        const activeRoles = computed(() => {
            if (resolvedContainer.value == null) {
                return null;
            }
            return activeContainerRoles(resolvedContainer.value.Code)?.filter(i => !isRoleHidden(i.RoleName));
        });
        const onlyImplicitRoles = computed(() => {
            return (implicitRoles.value || []).filter(i => !(activeRoles.value || []).some(j => i.RoleName == j.RoleName));
        });
        const implicitRoles = computed(() => {
            if (resolvedContainer.value == null) {
                return null;
            }
            const map = (i: RoleDtoExtended) => ({ r: i.RoleName, c: i.Container });
            return objectDistinct(
                implicitContainerRoles(resolvedContainer.value.Code)?.filter(i => !isRoleHidden(i.RoleName)),
                map,
            );
        });

        const resolvedContainer = computedAsync(async () => {
            return await containerPromise.value;
        });
        const resolvedUserData = computedAsync(async () => {
            return await mainStore.userDataPromise;
        });

        const loadRootContainer = async () => {
            return (rootContainerPromise.value = (async () => {
                const code = mainStore.tenantData.tenantCode;
                const getTenantHierarchyDataUrl = await mainStore.getUrl("getTenantHierarchyData", { tenantCode: code, type: HierarchyType.Security });
                const hierarchyDataResponsePromise = fetch(getTenantHierarchyDataUrl);
                const hierarchyDataResponse = await hierarchyDataResponsePromise;
                throwIfNonSuccessStatus(hierarchyDataResponse);
                let result = await hierarchyDataResponse.json();
                if (code == "default") {
                    const userData = await mainStore.userDataPromise;
                    if (userData.CanManageSystemUsers) {
                        result = {
                            Code: props.rootCode,
                            DisplayLabel: "AccessGov Root Container",
                            Subtenants: [result],
                        } as TenantHierarchyData;
                    }
                }
                return result;
            })());
        };
        const save = async () => {
            savePromise.value = setContainerRoleNames(resolvedContainer.value!.Code, editRoles.value);
            await savePromise.value;
            editing.value = false;
        };
        const isRoleHidden = (roleName: string) => {
            if (roleName == "TenantManager") {
                const userData = resolvedUserData.value;
                return !userData.CanGrantTenantManager;
            }
            return false;
        };

        watch(
            () => editing.value,
            newValue => {
                if (newValue) {
                    editRoles.value = (activeRoles.value || []).map(i => i.RoleName);
                    implicitEditRoles.value = (implicitRoles.value || []).map(i => i.RoleName);
                } else {
                    savePromise.value = null;
                }
            },
        );

        if (props.container == null) {
            loadRootContainer();
        }

        return {
            // States
            editing,
            editRoles,
            implicitEditRoles,
            savePromise,

            // Computed
            containerPromise,
            orderedRoles,
            activeRoles,
            onlyImplicitRoles,
            implicitRoles,

            // Methods
            save,

            // Injections
            getContainerLabel,
            getRoleDisplayLabel,
        };
    },
});
</script>
